import { useMemo } from 'react';
import { formatIQD, formatDateTime, formatDuration } from '../../../shared/format.js';
import { Icon } from '../../../layout/Icon.jsx';
import { useAuth } from '../../../shared/useAuth.js';
import { PrintLogo } from '../../../shared/PrintLogo.jsx';
import { useComprehensive } from '../hooks/useReports.js';
import './print.css';

const PAYMENT_LABELS = {
  cash: 'نقدي',
  card_balance: 'رصيد كارت',
  on_session: 'على الجلسة',
  external: 'خارجي',
  bank_transfer: 'تحويل بنكي',
  unknown: '—'
};

function pct(part, total) {
  if (!total) return '0%';
  return `${Math.round((part / total) * 100)}%`;
}

function Section({ title, sub, children, pageBreakBefore }) {
  return (
    <section
      className={`print-section ${pageBreakBefore ? 'print-page-break' : ''}`}
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 10,
        padding: 16,
        marginBottom: 14
      }}
    >
      <div style={{ marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid #FF7A1A' }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>{title}</h3>
        {sub ? <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{sub}</div> : null}
      </div>
      {children}
    </section>
  );
}

function KpiCard({ label, value, accent }) {
  return (
    <div
      className="kpi-card"
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 10,
        padding: '12px 14px',
        background: '#fff'
      }}
    >
      <div style={{ fontSize: 11.5, color: '#6b7280', fontWeight: 600 }}>{label}</div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 800,
          marginTop: 4,
          color: accent || '#111827',
          letterSpacing: '-.02em'
        }}
      >
        {value}
      </div>
    </div>
  );
}

function exportToXLSX(data, periodLabel) {
  // lazy-load to keep the initial bundle small.
  return import('xlsx').then((XLSXMod) => {
    const XLSX = XLSXMod.default || XLSXMod;
    const wb = XLSX.utils.book_new();
    wb.Props = { Title: 'تقرير شامل', Author: 'Kids Zone', CreatedDate: new Date() };

    function add(sheetName, rows, header) {
      if (!rows || !rows.length) {
        const empty = XLSX.utils.aoa_to_sheet([[`لا بيانات لـ ${sheetName}`]]);
        empty['!rtl'] = true;
        XLSX.utils.book_append_sheet(wb, empty, sheetName.slice(0, 31));
        return;
      }
      const ws = XLSX.utils.json_to_sheet(rows, { header });
      ws['!rtl'] = true;
      XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
    }

    // 1. ملخص — بنود أولياء الأمور تُضاف فقط إذا وفّرها الخادم
    // (خادم قديم لم يُعد نشره: تُحذف بدل عرض أصفار مضللة).
    const summaryRows = [
      { البند: 'الفترة', القيمة: periodLabel },
      { البند: 'إجمالي الإيرادات (د.ع)', القيمة: data.summary?.totalRevenue },
      { البند: 'إجمالي المصاريف (د.ع)', القيمة: data.summary?.totalExpenses },
      { البند: 'صافي الربح (د.ع)', القيمة: data.summary?.netProfit },
      { البند: 'إجمالي الفواتير', القيمة: data.summary?.totalInvoices },
      { البند: 'متوسط الفاتورة (د.ع)', القيمة: data.summary?.avgInvoice },
      { البند: 'إجمالي الزيارات', القيمة: data.summary?.totalVisits },
      { البند: 'أطفال فريدون', القيمة: data.summary?.uniqueChildren },
      { البند: 'إجمالي الجلسات', القيمة: data.summary?.totalSessions },
      { البند: 'متوسط مدة الجلسة (د)', القيمة: data.summary?.avgSessionDurationMin }
    ];
    if (data.guardians?.totals) {
      summaryRows.push(
        { البند: 'أولياء أمور في الفترة', القيمة: data.guardians.totals.guardians },
        { البند: 'أطفال في الفترة', القيمة: data.guardians.totals.children },
        { البند: 'رسوم لعب أولياء الأمور (د.ع)', القيمة: data.guardians.totals.playPaid ?? 0 },
        { البند: 'مسحوبات الكافتيريا (د.ع)', القيمة: data.guardians.totals.cafeteriaPaid ?? 0 },
        { البند: 'إجمالي فواتير أولياء الأمور (د.ع)', القيمة: data.guardians.totals.invoicesAmount }
      );
    }
    add('ملخص', summaryRows);

    // 2. الإيرادات حسب القسم
    add('الإيرادات حسب القسم',
      (data.revenue?.byCategory || []).map((c) => ({
        القسم: c.category,
        المبلغ: c.amount,
        'عدد العمليات': c.count
      }))
    );

    // 3. الإيرادات حسب الكاشير
    add('أداء الكاشيرات',
      (data.revenue?.byCashier || []).map((c) => ({
        الكاشير: c.cashierName,
        'عدد الفواتير': c.invoicesCount,
        المبيعات: c.salesTotal,
        المرتجعات: c.refundsTotal,
        'النقد المتوقع': c.expectedCash,
        نقدي: c.byMethod?.cash || 0,
        'رصيد كارت': c.byMethod?.card_balance || 0,
        'على الجلسة': c.byMethod?.on_session || 0,
        خارجي: c.byMethod?.external || 0
      }))
    );

    // 4. المصاريف حسب الفئة
    add('المصاريف حسب الفئة',
      (data.expenses?.byCategory || []).map((c) => ({
        الفئة: c.name,
        المبلغ: c.amount,
        'عدد العمليات': c.count
      }))
    );

    // 5. المصاريف التفصيلية
    add('المصاريف التفصيلية',
      (data.expenses?.list || []).map((e) => ({
        التاريخ: e.date ? new Date(e.date).toISOString().slice(0, 16).replace('T', ' ') : '',
        الفئة: e.category,
        الوصف: e.description,
        المبلغ: e.amount,
        أُنشئت_بواسطة: e.createdBy
      }))
    );

    // 6. الجدول الزمني اليومي
    add('الجدول الزمني اليومي',
      (data.revenue?.byDay || []).map((d) => ({
        التاريخ: d.date,
        الإيراد: d.revenue,
        المصاريف: d.expense,
        الصافي: (d.revenue || 0) - (d.expense || 0)
      }))
    );

    // 7. أفضل المنتجات
    add('أفضل المنتجات',
      (data.sales?.topProducts || []).map((p) => ({
        المنتج: p.name || p.productName,
        الكمية: p.qty ?? p.quantity ?? 0,
        الإيراد: p.revenue ?? p.amount ?? 0,
        'النسبة %': (p.percentOfTotal ?? p.percentage) != null
          ? `${Math.round(p.percentOfTotal ?? p.percentage)}%`
          : ''
      }))
    );

    // 8. الجلسات اليومية
    add('الجلسات اليومية',
      (data.sessions?.byDay || []).map((s) => ({
        التاريخ: s.date,
        'عدد الجلسات': s.count,
        الإيراد: s.revenue
      }))
    );

    // 8ب. أولياء الأمور والأطفال — سطر لكل طفل مع بيانات ولي أمره.
    // تُنشأ الورقة فقط إذا وفّر الخادم القسم (كما في العرض على الشاشة).
    if (data.guardians) {
      add('الأطفال وأولياء الأمور',
        (data.guardians.list || []).flatMap((g) =>
          (g.children || []).map((c) => ({
            'ولي الأمر': g.name,
            الهاتف: g.phone,
            الطفل: c.name,
            العمر: c.age != null ? c.age : '',
            الزيارات: c.visits,
            'رسوم اللعب (د.ع)': c.playPaid ?? c.playAmount,
            'الكافتيريا (د.ع)': c.cafeteriaPaid ?? 0,
            'عدد الفواتير': c.invoicesCount,
            'إجمالي الفواتير (د.ع)': c.invoicesAmount,
            'آخر زيارة': c.lastVisit
              ? new Date(c.lastVisit).toISOString().slice(0, 16).replace('T', ' ')
              : ''
          }))
        )
      );
    }

    // 9. حركة القاصات
    add('حركة القاصات',
      (data.cashFlow?.byCashbox || []).map((c) => ({
        القاصة: c.name,
        النوع: c.type,
        'الرصيد الحالي': c.currentBalance,
        'مبيعات نقدية': c.salesInflow ?? '',
        'تحويلات واردة (ليست إيراداً)': c.transfersIn ?? '',
        'تحويلات صادرة (ليست مصروفاً)': c.transfersOut ?? '',
        'إجمالي الإدخال': c.inflow,
        'إجمالي السحب': c.outflow,
        'الصافي': c.netMovement
      }))
    );

    // 10. الفواتير الملغاة
    add('الفواتير الملغاة',
      (data.sales?.cancelledList || []).map((c) => ({
        رقم: c.invoiceNumber,
        التاريخ: c.createdAt ? new Date(c.createdAt).toISOString().slice(0, 16).replace('T', ' ') : '',
        النوع: c.posType,
        المبلغ: c.grandTotal,
        الكاشير: c.cashierName,
        السبب: c.cancelReason || ''
      }))
    );

    const fname = `تقرير-شامل-${periodLabel.replace(/[^\w-]/g, '_')}.xlsx`;
    XLSX.writeFile(wb, fname);
  });
}

export function ComprehensiveReport({ range }) {
  const { data, isLoading, error } = useComprehensive(range);
  const user = useAuth((s) => s.user);

  const periodLabel = useMemo(() => {
    if (!range?.from || !range?.to) return '';
    const f = String(range.from).slice(0, 10);
    const t = String(range.to).slice(0, 10);
    return f === t ? f : `${f} → ${t}`;
  }, [range]);

  if (isLoading) {
    return <div className="text-center py-12 text-ink-3">جاري تحميل التقرير الشامل...</div>;
  }
  if (error || !data) {
    return <div className="text-center py-12 text-red">تعذّر تحميل التقرير الشامل</div>;
  }

  // كل قسم بقيمة افتراضية: استجابة ناقصة (خادم أقدم، أو قسم فشل) يجب أن
  // تُخفي القسم لا أن تُبيّض الصفحة بـ TypeError.
  const summary = data.summary || {};
  const revenue = {
    breakdown: { play: 0, cafeteria: 0, toys: 0, manual: 0, ...(data.revenue?.breakdown || {}) },
    byCategory: data.revenue?.byCategory || [],
    byDay: data.revenue?.byDay || [],
    byCashier: data.revenue?.byCashier || []
  };
  const expenses = {
    total: data.expenses?.total || 0,
    byCategory: data.expenses?.byCategory || [],
    list: data.expenses?.list || []
  };
  const sales = {
    totalInvoices: data.sales?.totalInvoices || 0,
    totalSales: data.sales?.totalSales || 0,
    avgInvoice: data.sales?.avgInvoice || 0,
    topProducts: data.sales?.topProducts || [],
    cancelledCount: data.sales?.cancelledCount || 0,
    cancelledAmount: data.sales?.cancelledAmount || 0,
    cancelledList: data.sales?.cancelledList || []
  };
  const sessions = {
    total: data.sessions?.total || 0,
    currentlyInside: data.sessions?.currentlyInside || 0,
    uniqueChildren: data.sessions?.uniqueChildren || 0,
    avgDurationMin: data.sessions?.avgDurationMin || 0,
    byDay: data.sessions?.byDay || []
  };
  const cashFlow = { byCashbox: data.cashFlow?.byCashbox || [] };
  // متوافق مع خادم قديم لم يُعد نشره بعد — القسم يختفي بدل أن يكسر الصفحة.
  const guardians = data.guardians || { totals: null, list: [] };
  const isEmptyPeriod =
    !summary.totalRevenue && !summary.totalExpenses && !sales.totalInvoices && !sessions.total;

  const totalsRevenueCheck =
    revenue.breakdown.play + revenue.breakdown.cafeteria + revenue.breakdown.toys + revenue.breakdown.manual;

  return (
    <div className="space-y-3">
      {/* شريط الأدوات — لا يُطبع */}
      <div
        className="no-print"
        style={{
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
          flexWrap: 'wrap'
        }}
      >
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => window.print()}
        >
          <Icon name="print" size={14} /> طباعة (A4)
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => exportToXLSX(data, periodLabel)}
        >
          📊 تصدير Excel
        </button>
      </div>

      <div className="print-root">
        {/* شعار المركز من الإعدادات — أعلى الورقة ووسطها */}
        <PrintLogo />
        {/* رأس التقرير */}
        <section
          className="print-section"
          style={{
            background: 'linear-gradient(135deg,#FF7A1A,#FFB627)',
            color: '#fff',
            borderRadius: 10,
            padding: 18,
            marginBottom: 14
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src="/kidzo-mark.png" alt="" width={48} height={48} style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,.2))' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 22, fontWeight: 800 }}>Kids Zone</div>
              <div style={{ fontSize: 13, opacity: 0.9 }}>تقرير شامل لإدارة قاعة الألعاب</div>
            </div>
            <div style={{ textAlign: 'end', fontSize: 11 }}>
              <div>الفترة: <b>{periodLabel}</b></div>
              <div>عدد الأيام: <b>{data.period.days}</b></div>
              <div>أُصدر: {formatDateTime(data.period.generatedAt)}</div>
              {user ? <div>المُصدِر: {user.fullName || user.username}</div> : null}
            </div>
          </div>
        </section>

        {isEmptyPeriod ? (
          <div
            style={{
              background: '#FFFBEB',
              border: '1px solid #FDE68A',
              color: '#92400E',
              borderRadius: 10,
              padding: 14,
              marginBottom: 14,
              textAlign: 'center',
              fontSize: 13,
              fontWeight: 700
            }}
          >
            لا توجد بيانات في هذه الفترة ({periodLabel}) — اختر مدى تاريخياً آخر.
          </div>
        ) : null}

        {/* الملخص التنفيذي */}
        <Section title="الملخص التنفيذي" sub="أبرز أرقام الفترة">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
              gap: 10
            }}
          >
            <KpiCard label="إجمالي الإيرادات" value={formatIQD(summary.totalRevenue)} accent="#15803D" />
            <KpiCard label="إجمالي المصاريف" value={formatIQD(summary.totalExpenses)} accent="#B91C1C" />
            <KpiCard
              label="صافي الربح"
              value={formatIQD(summary.netProfit)}
              accent={summary.netProfit >= 0 ? '#15803D' : '#B91C1C'}
            />
            <KpiCard label="عدد الزيارات" value={summary.totalVisits} accent="#0E7490" />
            <KpiCard label="عدد الفواتير" value={summary.totalInvoices} accent="#7C3AED" />
            <KpiCard label="متوسط الفاتورة" value={formatIQD(summary.avgInvoice)} accent="#C2410C" />
            <KpiCard label="إجمالي الجلسات" value={summary.totalSessions} accent="#0E7490" />
            <KpiCard
              label="متوسط مدة الجلسة"
              value={formatDuration(summary.avgSessionDurationMin || 0)}
              accent="#7C3AED"
            />
          </div>
        </Section>

        {/* توزيع الإيرادات */}
        <Section title="توزيع الإيرادات حسب القسم" sub="مساهمة كل نشاط في الإيراد الكلي">
          <table>
            <thead>
              <tr>
                <th>القسم</th>
                <th>المبلغ (د.ع)</th>
                <th>النسبة</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>اللعب والجلسات</td>
                <td>{formatIQD(revenue.breakdown.play)}</td>
                <td>{pct(revenue.breakdown.play, totalsRevenueCheck)}</td>
              </tr>
              <tr>
                <td>الكافتيريا</td>
                <td>{formatIQD(revenue.breakdown.cafeteria)}</td>
                <td>{pct(revenue.breakdown.cafeteria, totalsRevenueCheck)}</td>
              </tr>
              <tr>
                <td>محل الألعاب</td>
                <td>{formatIQD(revenue.breakdown.toys)}</td>
                <td>{pct(revenue.breakdown.toys, totalsRevenueCheck)}</td>
              </tr>
              <tr>
                <td>إيرادات يدوية</td>
                <td>{formatIQD(revenue.breakdown.manual)}</td>
                <td>{pct(revenue.breakdown.manual, totalsRevenueCheck)}</td>
              </tr>
              <tr style={{ fontWeight: 800, background: '#fef3c7' }}>
                <td>الإجمالي</td>
                <td>{formatIQD(totalsRevenueCheck)}</td>
                <td>100%</td>
              </tr>
            </tbody>
          </table>
        </Section>

        {/* أداء الكاشيرات */}
        <Section
          title="أداء الكاشيرات"
          sub="تفصيل المبيعات ومرتجعات الفترة لكل كاشير + تقسيم حسب طريقة الدفع"
          pageBreakBefore
        >
          {revenue.byCashier.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>لا توجد بيانات</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>الكاشير</th>
                  <th>الفواتير</th>
                  <th>المبيعات</th>
                  <th>المرتجعات</th>
                  <th>نقدي</th>
                  <th>رصيد كارت</th>
                  <th>على الجلسة</th>
                  <th>خارجي</th>
                  <th>النقد المتوقع</th>
                </tr>
              </thead>
              <tbody>
                {revenue.byCashier.map((c, i) => (
                  <tr key={c.cashierId || i}>
                    <td style={{ fontWeight: 700 }}>{c.cashierName}</td>
                    <td>{c.invoicesCount}</td>
                    <td>{formatIQD(c.salesTotal)}</td>
                    <td style={{ color: c.refundsTotal > 0 ? '#B91C1C' : undefined }}>
                      {formatIQD(c.refundsTotal)}
                    </td>
                    <td>{formatIQD(c.byMethod?.cash || 0)}</td>
                    <td>{formatIQD(c.byMethod?.card_balance || 0)}</td>
                    <td>{formatIQD(c.byMethod?.on_session || 0)}</td>
                    <td>{formatIQD(c.byMethod?.external || 0)}</td>
                    <td style={{ fontWeight: 700 }}>{formatIQD(c.expectedCash)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 800, background: '#fef3c7' }}>
                  <td>الإجمالي</td>
                  <td>{revenue.byCashier.reduce((s, c) => s + (c.invoicesCount || 0), 0)}</td>
                  <td>{formatIQD(revenue.byCashier.reduce((s, c) => s + (c.salesTotal || 0), 0))}</td>
                  <td>{formatIQD(revenue.byCashier.reduce((s, c) => s + (c.refundsTotal || 0), 0))}</td>
                  <td colSpan={4} />
                  <td>{formatIQD(revenue.byCashier.reduce((s, c) => s + (c.expectedCash || 0), 0))}</td>
                </tr>
              </tbody>
            </table>
          )}
        </Section>

        {/* المصاريف */}
        <Section title="المصاريف حسب الفئة" sub="توزيع المصاريف خلال الفترة">
          {expenses.byCategory.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 16, color: '#6b7280' }}>لا توجد مصاريف</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>الفئة</th>
                  <th>عدد العمليات</th>
                  <th>المبلغ (د.ع)</th>
                  <th>النسبة</th>
                </tr>
              </thead>
              <tbody>
                {expenses.byCategory.map((e, i) => (
                  <tr key={i}>
                    <td>{e.name}</td>
                    <td>{e.count}</td>
                    <td>{formatIQD(e.amount)}</td>
                    <td>{pct(e.amount, expenses.total)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 800, background: '#fef3c7' }}>
                  <td>الإجمالي</td>
                  <td>{expenses.byCategory.reduce((s, e) => s + (e.count || 0), 0)}</td>
                  <td>{formatIQD(expenses.total)}</td>
                  <td>100%</td>
                </tr>
              </tbody>
            </table>
          )}
        </Section>

        {/* المصاريف التفصيلية */}
        {expenses.list.length > 0 ? (
          <Section title="المصاريف التفصيلية" sub={`${expenses.list.length} عملية`}>
            <table>
              <thead>
                <tr>
                  <th>التاريخ</th>
                  <th>الفئة</th>
                  <th>الوصف</th>
                  <th>المبلغ</th>
                  <th>المُسجِّل</th>
                </tr>
              </thead>
              <tbody>
                {expenses.list.slice(0, 50).map((e, i) => (
                  <tr key={i}>
                    <td style={{ direction: 'ltr', fontSize: '0.92em', whiteSpace: 'nowrap' }}>
                      {formatDateTime(e.date)}
                    </td>
                    <td>{e.category}</td>
                    <td>{e.description || '—'}</td>
                    <td style={{ fontWeight: 700 }}>{formatIQD(e.amount)}</td>
                    <td>{e.createdBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {expenses.list.length > 50 ? (
              <div style={{ fontSize: 10.5, color: '#6b7280', marginTop: 6 }}>
                * عُرضت أول 50 عملية فقط — صدِّر إلى Excel للقائمة الكاملة.
              </div>
            ) : null}
          </Section>
        ) : null}

        {/* عمليات الجلسات */}
        <Section title="عمليات اللعب والجلسات" sub="إحصائيات الزيارات والمدد" pageBreakBefore>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10, marginBottom: 12 }}>
            <KpiCard label="إجمالي الجلسات" value={sessions.total} />
            <KpiCard label="حالياً داخل القاعة" value={sessions.currentlyInside} />
            <KpiCard label="أطفال فريدون" value={sessions.uniqueChildren} />
            <KpiCard label="متوسط المدة" value={formatDuration(sessions.avgDurationMin || 0)} />
          </div>
          {sessions.byDay.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>التاريخ</th>
                  <th>عدد الجلسات</th>
                  <th>إيراد دخول الجلسات (د.ع)</th>
                </tr>
              </thead>
              <tbody>
                {sessions.byDay.map((s, i) => (
                  <tr key={i}>
                    <td style={{ direction: 'ltr', whiteSpace: 'nowrap' }}>{s.date}</td>
                    <td>{s.count}</td>
                    <td>{formatIQD(s.revenue || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </Section>

        {/* أولياء الأمور والأطفال */}
        {guardians.totals ? (
          <Section
            title="أولياء الأمور والأطفال"
            sub="أسماء الأطفال وأولياء أمورهم وزياراتهم وإجمالي فواتيرهم خلال الفترة"
            pageBreakBefore
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0,1fr))', gap: 10, marginBottom: 12 }}>
              <KpiCard label="أولياء الأمور" value={guardians.totals.guardians} accent="#0E7490" />
              <KpiCard label="الأطفال" value={guardians.totals.children} accent="#7C3AED" />
              <KpiCard label="الزيارات" value={guardians.totals.visits} accent="#C2410C" />
              <KpiCard label="رسوم اللعب" value={formatIQD(guardians.totals.playPaid ?? guardians.totals.playAmount)} accent="#0E7490" />
              <KpiCard label="الكافتيريا" value={formatIQD(guardians.totals.cafeteriaPaid ?? 0)} accent="#C2410C" />
              <KpiCard label="إجمالي فواتيرهم" value={formatIQD(guardians.totals.invoicesAmount)} accent="#15803D" />
            </div>
            {guardians.list.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 16, color: '#6b7280' }}>لا توجد زيارات أو فواتير في الفترة</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>ولي الأمر</th>
                    <th>الهاتف</th>
                    <th>الطفل</th>
                    <th>العمر</th>
                    <th>الزيارات</th>
                    <th>رسوم اللعب</th>
                    <th>الكافتيريا</th>
                    <th>الفواتير</th>
                    <th>الإجمالي (د.ع)</th>
                  </tr>
                </thead>
                <tbody>
                  {guardians.list.slice(0, 60).map((g) => {
                    const rows = g.children || [];
                    return rows.map((c, ci) => (
                      <tr key={`${g.guardianId}-${c.childId || 'none'}`}>
                        {ci === 0 ? (
                          <>
                            <td rowSpan={rows.length} style={{ fontWeight: 700 }}>{g.name}</td>
                            <td rowSpan={rows.length} style={{ direction: 'ltr', fontFamily: 'monospace' }}>{g.phone}</td>
                          </>
                        ) : null}
                        <td style={!c.childId ? { color: '#6b7280', fontStyle: 'italic' } : undefined}>{c.name}</td>
                        <td>{c.age != null ? c.age : '—'}</td>
                        <td>{c.visits}</td>
                        <td>{formatIQD(c.playPaid ?? c.playAmount)}</td>
                        <td style={{ color: '#C2410C' }}>{formatIQD(c.cafeteriaPaid ?? 0)}</td>
                        <td>{c.invoicesCount}</td>
                        <td style={{ fontWeight: 700 }}>{formatIQD(c.invoicesAmount)}</td>
                      </tr>
                    ));
                  })}
                  <tr style={{ fontWeight: 800, background: '#fef3c7' }}>
                    <td colSpan={2}>الإجمالي ({guardians.totals.guardians} ولي أمر)</td>
                    <td>{guardians.totals.children} طفل</td>
                    <td />
                    <td>{guardians.totals.visits}</td>
                    <td>{formatIQD(guardians.totals.playPaid ?? guardians.totals.playAmount)}</td>
                    <td>{formatIQD(guardians.totals.cafeteriaPaid ?? 0)}</td>
                    <td>{guardians.totals.invoicesCount}</td>
                    <td>{formatIQD(guardians.totals.invoicesAmount)}</td>
                  </tr>
                </tbody>
              </table>
            )}
            {guardians.list.length > 60 ? (
              <div style={{ fontSize: 10.5, color: '#6b7280', marginTop: 6 }}>
                * عُرض أول 60 ولي أمر فقط — صدِّر إلى Excel للقائمة الكاملة ({guardians.list.length}).
              </div>
            ) : null}
          </Section>
        ) : null}

        {/* أفضل المنتجات */}
        {sales.topProducts && sales.topProducts.length > 0 ? (
          <Section title="أفضل المنتجات مبيعاً" sub={`أعلى ${sales.topProducts.length} منتجاً`}>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>المنتج</th>
                  <th>الكمية</th>
                  <th>الإيراد (د.ع)</th>
                  <th>النسبة</th>
                </tr>
              </thead>
              <tbody>
                {sales.topProducts.map((p, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{p.name || p.productName}</td>
                    <td>{p.qty ?? p.quantity ?? 0}</td>
                    <td style={{ fontWeight: 700 }}>{formatIQD(p.revenue ?? p.amount ?? 0)}</td>
                    <td>
                      {(p.percentOfTotal ?? p.percentage) != null
                        ? `${Math.round(p.percentOfTotal ?? p.percentage)}%`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        ) : null}

        {/* حركة القاصات */}
        {cashFlow.byCashbox && cashFlow.byCashbox.length > 0 ? (
          <Section
            title="حركة القاصات"
            sub="إدخال وسحب وأرصدة الفترة — التحويل بين القاصات نقل نقد لا إيراد ولا مصروف، ولا يدخل في أي إجمالي أعلاه"
          >
            <table>
              <thead>
                <tr>
                  <th>القاصة</th>
                  <th>النوع</th>
                  <th>الرصيد الحالي</th>
                  <th>إدخال</th>
                  <th>سحب</th>
                  <th>الصافي</th>
                </tr>
              </thead>
              <tbody>
                {cashFlow.byCashbox.map((c, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 700 }}>{c.name}</td>
                    <td>{c.type}</td>
                    <td>{formatIQD(c.currentBalance || 0)}</td>
                    <td style={{ color: '#15803D' }}>{formatIQD(c.inflow || 0)}</td>
                    <td style={{ color: '#B91C1C' }}>{formatIQD(c.outflow || 0)}</td>
                    <td style={{ fontWeight: 700, color: c.netMovement >= 0 ? '#15803D' : '#B91C1C' }}>
                      {formatIQD(c.netMovement || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        ) : null}

        {/* الجدول الزمني اليومي */}
        {revenue.byDay && revenue.byDay.length > 0 ? (
          <Section title="الجدول الزمني اليومي" sub="إيراد ومصاريف وصافي ربح كل يوم">
            <table>
              <thead>
                <tr>
                  <th>التاريخ</th>
                  <th>الإيراد</th>
                  <th>المصاريف</th>
                  <th>الصافي</th>
                </tr>
              </thead>
              <tbody>
                {revenue.byDay.map((d, i) => {
                  const net = (d.revenue || 0) - (d.expense || 0);
                  return (
                    <tr key={i}>
                      <td style={{ direction: 'ltr', whiteSpace: 'nowrap' }}>{d.date}</td>
                      <td>{formatIQD(d.revenue || 0)}</td>
                      <td>{formatIQD(d.expense || 0)}</td>
                      <td style={{ fontWeight: 700, color: net >= 0 ? '#15803D' : '#B91C1C' }}>
                        {formatIQD(net)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Section>
        ) : null}

        {/* الفواتير الملغاة */}
        {sales.cancelledList && sales.cancelledList.length > 0 ? (
          <Section title="الفواتير الملغاة" sub={`${sales.cancelledCount} فاتورة بقيمة ${formatIQD(sales.cancelledAmount)}`}>
            <table>
              <thead>
                <tr>
                  <th>رقم</th>
                  <th>التاريخ</th>
                  <th>النوع</th>
                  <th>الكاشير</th>
                  <th>المبلغ</th>
                  <th>السبب</th>
                </tr>
              </thead>
              <tbody>
                {sales.cancelledList.slice(0, 30).map((c, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'monospace' }}>{c.invoiceNumber}</td>
                    <td style={{ direction: 'ltr', whiteSpace: 'nowrap' }}>{formatDateTime(c.createdAt)}</td>
                    <td>{c.posType}</td>
                    <td>{c.cashierName}</td>
                    <td>{formatIQD(c.grandTotal || 0)}</td>
                    <td>{c.cancelReason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        ) : null}

        {/* تذييل */}
        <section style={{ marginTop: 12, fontSize: 10.5, color: '#6b7280', textAlign: 'center' }}>
          <div>هذا تقرير آلي مُصدر من نظام Kids Zone — {formatDateTime(data.period.generatedAt)}</div>
          {user ? <div>المُصدر: {user.fullName || user.username}</div> : null}
        </section>
      </div>
    </div>
  );
}
